import type { ContentCard } from "./server";

export const RESOURCE_CATEGORIES = [
  "Mapping Guides",
  "Coordinates & Geocoding",
  "Routing & Logistics",
  "Data Visualization",
  "Sales & Territory Mapping",
  "Google Maps & Alternatives",
  "Industry Use Cases",
  "Geography & Map Collections",
  "Product Features & Updates",
] as const;
export type ResourceCategory = (typeof RESOURCE_CATEGORIES)[number];

export const RESOURCE_TYPES = [
  "Step-by-step guide",
  "Concept & reference",
  "Interactive example",
  "Comparison",
  "Industry workflow",
  "Product tutorial",
] as const;
export type ResourceType = (typeof RESOURCE_TYPES)[number];

export interface ResourceItem extends ContentCard {
  category: ResourceCategory;
  contentType: ResourceType;
  featured: boolean;
}

const FEATURED_SLUGS = new Set([
  "plan-the-most-efficient-route-a-guide-to-smarter-navigation",
  "plot-coordinates-on-map-find-places-with-pinpoint-accuracy",
  "zip-code-maps-get-deep-insights-from-numeric-identifiers",
  "how-to-create-a-custom-map-in-google-maps",
  "free-map-generator-tools-to-make-quality-charts-at-no-cost",
  "canadian-provinces-map-exploring-canadas-diversity",
  "driving-radius-map-make-better-business-decisions",
  "baseball-team-map-mapping-popular-ballparks",
  "geospatial-data-what-is-it-and-how-do-you-use-it",
  "what-is-a-thematic-map-an-all-in-one-guide-for-beginners",
  "worlds-tallest-building-exploring-sky-high-structures",
  "wonders-of-the-world-ancient-modern-and-natural-wonders",
  "states-and-capitals-map-unlocking-the-geography-of-the-us",
  "map-of-the-us-with-cities",
  "creepy-google-earth-coordinates-that-would-fascinate-you",
  "heat-map-generation-from-excel-location-data",
  "sales",
  "route-66-map-discover-the-historic-route-in-america",
  "maptive-vs-mapize",
  "batchgeo-vs-mapize",
  "create-a-map-from-latitude-and-longitude-coordinates",
  "map-coordinates",
  "create-interactive-maps-from-data",
  "how-to-export-your-map",
]);

function includesAny(text: string, terms: string[]): boolean {
  return terms.some((term) => text.includes(term));
}

export function classifyResource(card: Pick<ContentCard, "slug" | "title" | "metaDescription">): {
  category: ResourceCategory;
  contentType: ResourceType;
} {
  const text = `${card.slug} ${card.title} ${card.metaDescription ?? ""}`.toLowerCase();

  let category: ResourceCategory = "Mapping Guides";
  if (includesAny(text, ["latitude", "longitude", "coordinate", "geocod", "address"])) {
    category = "Coordinates & Geocoding";
  } else if (includesAny(text, ["route", "delivery", "distance", "travel", "trip", "drive time", "driving radius"])) {
    category = "Routing & Logistics";
  } else if (includesAny(text, ["sales", "territor", "customer", "market", "business"])) {
    category = "Sales & Territory Mapping";
  } else if (includesAny(text, ["heat map", "visualization", "cluster", "thematic", "choropleth", "data analysis"])) {
    category = "Data Visualization";
  } else if (includesAny(text, ["google maps", "google earth", "mapquest", "batchgeo", "maptive", "mappoint", "alternative"])) {
    category = "Google Maps & Alternatives";
  } else if (includesAny(text, ["real estate", "journal", "disaster", "supply chain", "store locator", "industry"])) {
    category = "Industry Use Cases";
  } else if (includesAny(text, ["world", "cities", "states", "provinces", "wonders", "stadium", "baseball", "route 66", "geography"])) {
    category = "Geography & Map Collections";
  } else if (includesAny(text, ["mapize", "export", "embed", "edit map", "new feature", "mobile map", "map grouping"])) {
    category = "Product Features & Updates";
  }

  let contentType: ResourceType = "Concept & reference";
  if (includesAny(text, [" vs ", "alternative", "best mapping", "tools compared", "comparison"])) {
    contentType = "Comparison";
  } else if (includesAny(text, ["how to", "guide", "create", "make a", "plot", "enter", "steps"])) {
    contentType = "Step-by-step guide";
  } else if (includesAny(text, ["mapize", "export", "embed", "edit map", "new feature"])) {
    contentType = "Product tutorial";
  } else if (category === "Industry Use Cases" || category === "Sales & Territory Mapping") {
    contentType = "Industry workflow";
  } else if (category === "Geography & Map Collections") {
    contentType = "Interactive example";
  }

  return { category, contentType };
}

export function enrichResourceCard(card: ContentCard): ResourceItem {
  return { ...card, ...classifyResource(card), featured: FEATURED_SLUGS.has(card.slug) };
}

export interface LearningPath {
  id: string;
  title: string;
  description: string;
  categories: ResourceCategory[];
}

export const LEARNING_PATHS: LearningPath[] = [
  { id: "plot", title: "Plot locations and coordinates", description: "Turn addresses, coordinates, and spreadsheets into accurate interactive maps.", categories: ["Coordinates & Geocoding", "Mapping Guides"] },
  { id: "territories", title: "Build sales territories", description: "Organize customers, balance coverage, and visualize market opportunity.", categories: ["Sales & Territory Mapping"] },
  { id: "routes", title: "Plan routes and deliveries", description: "Compare stops, travel distances, delivery areas, and drive-time coverage.", categories: ["Routing & Logistics"] },
  { id: "analyze", title: "Analyze geographic data", description: "Reveal patterns using heat maps, clusters, themes, and location intelligence.", categories: ["Data Visualization"] },
  { id: "boundaries", title: "Create ZIP code and radius maps", description: "Work with postal areas, proximity circles, boundaries, and regional data.", categories: ["Mapping Guides", "Data Visualization"] },
  { id: "share", title: "Share, embed, and export maps", description: "Publish maps, embed them on a website, and move data into other tools.", categories: ["Product Features & Updates"] },
];
